/**
 * GeoService Property Report Mapper
 *
 * Maps the canonical /v4/geo/property/report envelope into the legacy
 * NSW-shaped object that property.service.js / userStore consumers expect.
 *
 * One canonical response feeds the legacy address-check and property-info
 * callers, so this mapper exposes both a full mapper and per-call helpers
 * that pick only the fields each caller needs.
 */

/**
 * A canonical control value envelope, e.g. planning_controls.zoning.
 * - present: provider returned a positive match
 * - checked: provider attempted to resolve this control
 * - value:   the resolved value (boolean for constraints, string/number otherwise)
 * @typedef {Object} CanonicalValue
 * @property {boolean} [present]
 * @property {boolean} [checked]
 * @property {*}       [value]
 * @property {string|null} [code]
 * @property {string|null} [label]
 * @property {string|null} [unit]
 */

/**
 * Resolve a canonical boolean constraint into a strict tri-state.
 *
 * Returns:
 *  - true   when value === true (constraint applies)
 *  - false  when value === false OR checked===true with no positive value
 *           (constraint was checked and confirmed absent)
 *  - null   when the provider did not check, so we genuinely do not know
 *
 * The null vs false distinction matters: legacy code branches on
 * "flood_lookup_successful" to decide whether to show "not affected" vs
 * "couldn't determine".
 *
 * @param {CanonicalValue|undefined|null} c
 * @returns {boolean|null}
 */
export function canonicalBoolean(c) {
    if (!c) return null;
    if (typeof c.value === 'boolean') return c.value;
    if (c.checked === true) return false;
    return null;
}

/**
 * Render a canonical value with unit as a display string ("450 m²", "9 m").
 * Returns '' when the value is null/empty so userStore string fields stay strings.
 *
 * @param {CanonicalValue|undefined|null} c
 * @returns {string}
 */
export function canonicalText(c) {
    if (!c || c.value == null || c.value === '') return '';
    return c.unit ? `${c.value} ${c.unit}` : String(c.value);
}

/**
 * INVARIANT: This value reaches QA property facts, so parcel precision must
 * survive formatting. Decimal strings retain digits beyond Number precision.
 *
 * @param {number|string|undefined|null} value
 * @returns {string}
 */
function squareMetresText(value) {
    if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? `${value} m²` : '';
    if (typeof value !== 'string') return '';
    const decimal = value.trim();
    if (!/^\d+(?:\.\d+)?$/.test(decimal) || !Number.isFinite(Number(decimal))) return '';
    return `${decimal} m²`;
}

/**
 * Map a canonical GeoService /v4/geo/property/report response into the
 * legacy property.service shape.
 *
 * Output keys mirror what _processAddressCheckResponse / _processPropertyInfoResponse
 * previously produced so downstream userStore.setProperty calls are unchanged.
 *
 * @param {Object|null|undefined} report - canonical /v4/geo/property/report response
 * @returns {Object|null}
 */
export function mapGeoPropertyReport(report) {
    if (!report || typeof report !== 'object') return null;

    const ab = report.admin_boundaries ?? {};
    const council = ab.council ?? {};
    const pc = report.planning_controls ?? {};
    const ec = report.environmental_constraints ?? {};
    const parcel = report.parcel ?? {};

    const zoningCode = pc.zoning?.code ?? (typeof pc.zoning?.value === 'string' ? pc.zoning.value : '');

    return {
        // address-check fields
        council_id: council.id ?? ab.council_id ?? '',
        council_name: council.name ?? ab.council_name ?? ab.local_government_area ?? '',
        council_url: council.url ?? ab.council_url ?? '',
        zone: zoningCode || '',
        zone_description: pc.zoning?.label ?? '',
        // INVARIANT: lot_size is the parcel's actual area; minimum_lot_size is
        // a separate planning control.
        lot_size: squareMetresText(parcel.area_sqm),
        floor_space_ratio: canonicalText(pc.floor_space_ratio),
        bushfire_restrictions: canonicalBoolean(ec.bushfire),
        flood_restrictions: canonicalBoolean(ec.flood),
        flood_lookup_successful: ec.flood?.checked === true,
        heritage_restrictions: canonicalBoolean(ec.heritage),
        mine_subsidence_affects: canonicalBoolean(ec.mine_subsidence),
        // HMS State Heritage Register item ID (drives the "more information"
        // ViewItem link). GeoService surfaces it only for State-listed items;
        // local-only heritage has no HMS page, so it stays '' and the link is
        // omitted. See daisy-chatbox heritage-only branch.
        heritage_item_id: ec.heritage?.metadata?.heritage_item_id ?? '',
        property_report_url: report.report_links?.[0]?.href ?? '',
        ...(report.diagnostics?.provider
            ? {
                  enrichment_incomplete:
                      report.diagnostics.provider_enrichment_complete === false ||
                      ['partial', 'failed'].includes(report.diagnostics.provider.status)
              }
            : {}),
        ...(report.diagnostics?.report_links
            ? {
                  report_link_unavailable: report.diagnostics.report_links.status === 'unavailable'
              }
            : {}),

        // property-info fields
        maximum_building_height: canonicalText(pc.height_of_building),
        minimum_lot_size: canonicalText(pc.minimum_lot_size),

        // lot-width fields. Canonical envelope returns the metric on parcel;
        // legacy callers receive a numeric `lot_width` or null.
        lot_width: parcel.lot_width ?? parcel.lot_width_m ?? parcel.frontage_m ?? null,

        // Parcel geometry flags — forwarded to user_config.project so the
        // AzureAPI prompt has them. Default to false when the envelope omits.
        is_battle_axe_lot: parcel.is_battle_axe_lot === true,
        is_corner_lot: parcel.is_corner_lot === true
    };
}
